/**
 * Pure helpers for the optimistic outbox UI layer.
 * No DOM, no side-effects — safe to unit-test directly.
 * Mirrors the freshness-bar.js style: pure functions, no exports of state.
 */

// ---------------------------------------------------------------------------
// pendingBadgeFor
// ---------------------------------------------------------------------------

/**
 * Return the badge descriptor for an item that is pending sync.
 * The caller uses `label` and `className` to build the badge element;
 * no DOM is created here so tests can inspect the descriptor directly.
 *
 * @param {string} key  The outbox key (outbox entry id / Idempotency-Key).
 * @returns {{ key: string, i18nKey: string, className: string }}
 */
export function pendingBadgeFor(key) {
  return {
    key,
    i18nKey: 'inventory.pending_badge',
    // Uses the same chip token palette as the open-badge so the two can
    // stack in the pill column without visual conflict.
    className: 'chip text-xs px-2 py-0.5 bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-400 border border-primary-100 dark:border-primary-700',
    dataRole: 'inventory-pending-badge',
  };
}

// ---------------------------------------------------------------------------
// applyOptimistic
// ---------------------------------------------------------------------------

/**
 * Return a NEW flat list of inventory items reflecting the optimistic change.
 * The input `list` is never mutated.
 *
 * Supported mutation shapes:
 *   { type: 'consume',  id, quantity }  — reduce quantity (or mark qty=0 when
 *                                         quantity == item.quantity)
 *   { type: 'discard',  id }            — mark quantity 0
 *   { type: 'move',     id, storage_location_id } — update location
 *   { type: 'patch',    id, patch }     — merge arbitrary fields
 *   { type: 'delete',   id }            — remove the row
 *   { type: 'add',      item }          — prepend a new pending row
 *
 * Every affected item gains `_pending: true` and `_outboxKey: key`.
 *
 * @param {object[]} list    Flat array of inventory item objects.
 * @param {{ type: string, key: string, [k: string]: unknown }} mutation
 * @returns {object[]}  New array.
 */
export function applyOptimistic(list, mutation) {
  const { type, key } = mutation;

  if (type === 'add') {
    const newItem = { ...mutation.item, _pending: true, _outboxKey: key, _optimisticAdd: true };
    return [newItem, ...list];
  }

  if (type === 'delete') {
    return list.filter((item) => item.id !== mutation.id);
  }

  return list.map((item) => {
    if (item.id !== mutation.id) return item;

    switch (type) {
      case 'consume': {
        const newQty = Math.max(0, Number(item.quantity) - Number(mutation.quantity));
        return { ...item, quantity: newQty, _pending: true, _outboxKey: key };
      }
      case 'discard':
        return { ...item, quantity: 0, _pending: true, _outboxKey: key };
      case 'move':
        return { ...item, storage_location_id: mutation.storage_location_id, _pending: true, _outboxKey: key };
      case 'patch':
        return { ...item, ...(mutation.patch || {}), _pending: true, _outboxKey: key };
      default:
        return { ...item, _pending: true, _outboxKey: key };
    }
  });
}

// ---------------------------------------------------------------------------
// reconcile
// ---------------------------------------------------------------------------

/**
 * Return a NEW list with the pending state resolved according to a server signal.
 *
 * Signal shapes:
 *   { type: 'confirm', key }   — clear _pending / _outboxKey for matching item(s)
 *   { type: 'conflict', key }  — replace _pending with _conflict: true
 *   { type: 'expired', key }   — remove the optimistic row (or clear pending if
 *                                the row pre-existed the outbox entry, identified
 *                                by having only _outboxKey set)
 *
 * @param {object[]} list
 * @param {{ type: 'confirm'|'conflict'|'expired', key: string }} signal
 * @returns {object[]}
 */
export function reconcile(list, signal) {
  const { type, key } = signal;

  return list.reduce((acc, item) => {
    if (item._outboxKey !== key) {
      acc.push(item);
      return acc;
    }

    if (type === 'confirm') {
      const { _pending, _outboxKey, _conflict, ...rest } = item;
      acc.push(rest);
      return acc;
    }

    if (type === 'conflict') {
      const { _pending, ...rest } = item;
      acc.push({ ...rest, _conflict: true });
      return acc;
    }

    if (type === 'expired') {
      // Drop purely-optimistic rows (no server-side counterpart yet).
      // Rows that existed before the optimistic overlay just get cleared.
      if (item._optimisticAdd) {
        // Skip (remove from list)
        return acc;
      }
      const { _pending, _outboxKey, _conflict, ...rest } = item;
      acc.push(rest);
      return acc;
    }

    acc.push(item);
    return acc;
  }, []);
}
